"use client";

import React, { useState, useRef, useEffect } from "react";
import Image, { StaticImageData } from "next/image";
import { Icons, Images } from "@assets/assets";
import PrimaryBtn from "@/components/PrimaryBtn";
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";

type ProjectItem = {
  title: React.ReactNode;
  description: string;
};

interface hiringItem {
  icon: string;
  title: string;
  description: string;
  image?: StaticImageData | string;
  link?: string;
}

interface projectArr {
  icon: string;
  title: string;
  description: string;
  image?: StaticImageData | string;
  link?: string;
}

interface CtaPanelProps {
  title: string;
  description: string;
}

interface HiringModelProps {
  subtitle: string;
  title: React.ReactNode;
  description: React.ReactNode;
  hiringArray: hiringItem[];
  ctaPanel: CtaPanelData;
  projectData: ProjectItem;
  projectArray: projectArr[];
}

type CtaPanelData = {
  title: string;
  description: string;
};

function HireCard({ icon, title, description }: hiringItem) {
  const [isTruncated, setIsTruncated] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      requestAnimationFrame(() => {
        if (textRef.current) {
          const { scrollHeight, clientHeight } = textRef.current;
          // Check if content is larger than the 4-line clamp
          setIsTruncated(scrollHeight > clientHeight + 2);
        }
      });
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [description]);

  return (
    <div className="w-full h-[500px] md:h-[600px] bg-[#fff] relative p-10 transition-all duration-300 flex flex-col justify-between group">
      <div className="flex flex-col gap-8 justify-start overflow-hidden">
        {/* Icon Section */}
        <div className="mb-2 shrink-0">
          <Image
            src={icon}
            alt={`${title} icon`}
            width={70}
            height={70}
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="industry-icon w-[clamp(4.5rem,1rem+1.5vw,5.5rem)] h-[clamp(4.5rem,1rem+1.5vw,5.5rem)] object-contain"
          />
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-4 overflow-hidden">
          <h3 className="text-[#00040b] text-[clamp(1.75rem,1.5rem+1vw,2.2rem)] font-medium clash-font capitalize leading-[120%] shrink-0">
            {title}
          </h3>

          <div
            className={`relative pr-2 transition-all duration-300 max-h-[220px] ${
              canScroll ? "overflow-y-auto custom-scrollbar" : "overflow-hidden"
            }`}
          >
            <p
              ref={textRef}
              className={`text-[18px] leading-[150%] text-[#00040B] opacity-80 ${
                !canScroll ? "line-clamp-4" : ""
              }`}
            >
              {description}
            </p>
          </div>

          {isTruncated && (
            <button
              onClick={() => setCanScroll(!canScroll)}
              className="flex items-center gap-2 mt-0 group/expand cursor-pointer focus:outline-none"
              aria-label={
                canScroll ? "Collapse description" : "Expand description"
              }
            >
              <div
                className={`transition-transform duration-300 ${canScroll ? "rotate-180" : "rotate-0"}`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#00040b]"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <span className="font-bold text-[#00040b] text-[14px] uppercase tracking-wide group-hover/expand:underline"></span>
            </button>
          )}
        </div>
      </div>

      {/* Bottom Arrow - Rotates on Card Hover */}
      <div className="mt-8 shrink-0">
        <div className="transition-transform duration-500 ease-in-out group-hover:rotate-180 inline-block">
          <Image
            src={Icons?.industryArrow}
            alt="Arrow icon"
            width={48}
            height={48}
            className="w-12 h-12"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d9e2ed;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
}

function CtaPanel({ title, description }: CtaPanelProps) {
  const { handleOpenQuote } = useTheme();
  return (
    <div className="bg-[#225BD4] p-10 lg:p-12 flex flex-col items-center justify-between text-center relative h-full min-h-[500px] md:min-h-[600px]">
      <div className="z-10 flex flex-col items-center space-y-6">
        <span className="text-white/80 text-[13px] font-bold tracking-widest uppercase">
          Looking for
        </span>
        <h3 className="text-[32px] lg:text-[35px] font-semibold clash-font text-white leading-tight max-w-[12ch]">
          {title}
        </h3>
        <p className="text-white/80 text-[16px] max-w-[22ch]">{description}</p>
        <PrimaryBtn
          text="Let’s Connect"
          dark={true}
          iconSrc={Icons?.chevronBlue}
          onClick={() => {
            handleOpenQuote();
          }}
        />
      </div>
      <div className="mt-8 z-10">
        <Image
          src={Images?.laravel_need_a_price}
          alt="Get quote"
          width={180}
          height={70}
          sizes="(max-width: 1024px) 100vw, 50vw"
        />
      </div>
    </div>
  );
}

export default function HiringModels({
  title,
  description,
  subtitle,
  hiringArray = [],
  ctaPanel,
  projectData,
  projectArray = [],
}: HiringModelProps) {
  return (
    <section className="bg-[#D9E2ED] py-16 lg:py-32 relative z-2 overflow-hidden">
      <div className="max-w-full mx-auto px-[5%]">
        <span className="text-[#00040b] text-[14px] font-bold tracking-[0.2em] uppercase block mb-5">
          {subtitle}
        </span>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 lg:mb-24 items-start">
          <div className="space-y-6">
            <h2 className="text-[35px] lg:text-[72px] leading-[1.1] font-medium clash-font text-[#00040b]">
              {title}
            </h2>
          </div>
          <div className="flex flex-col">
            <div className="text-[18px] lg:text-[20px] leading-[1.6] text-[#00040b] opacity-80 lg:max-w-full">
              {description}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {hiringArray?.map((model: any, i: number) => (
            <HireCard
              key={`hiring-${i}`}
              icon={model.icon}
              title={model.title}
              description={model.description}
            />
          ))}

          <CtaPanel
            title={ctaPanel?.title}
            description={ctaPanel?.description}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-24 lg:mt-40 items-stretch">
          <div className="xl:col-span-6 flex flex-col justify-start space-y-10 pr-0 lg:pr-16 mb-12 xl:mb-0">
            <h3 className="text-[35px] lg:text-[64px] leading-[1.1] font-medium clash-font text-[#00040b]">
              {projectData?.title}
            </h3>
            <p className="text-[18px] lg:text-[20px] leading-[1.6] text-[#636364]">
              {projectData?.description}
            </p>
          </div>

          {projectArray?.map((model, i) => (
            <div key={`project-${i}`} className="xl:col-span-3 md:col-span-6">
              <HireCard
                icon={model.icon}
                title={model.title}
                description={model.description}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
